import { useState, useMemo, useCallback, ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"
import { CreateSystemButton } from "@hydrologiq/shared/content/components/Button"
import MultiScreenDialog, { Screen } from "@hydrologiq/shared/components/navigation/MultiScreenDialog"
import SystemCreationForm from "@hydrologiq/shared/components/creation/SystemCreationForm"
import CustomerCreationForm from "@hydrologiq/shared/components/creation/CustomerCreationForm"
import SimpleSiteCreationForm from "./SimpleSiteCreationForm"
import {
  CustomerSiteSelectorProvider,
  useCustomerSiteSelectorState,
  useCustomerSiteSelectorActions,
} from "@hydrologiq/shared/components/selection/CustomerSiteSelectorProvider"

interface SystemCreateProps {
  /** Pre-set customer ID (skips customer selection) */
  customerId?: string
  /** Pre-set site ID (skips site selection) */
  siteId?: string
}

type ScreenId = "create-system" | "create-customer" | "create-site"

/**
 * System creation dialog component.
 * Uses MultiScreenDialog with the Provider prop to inject CustomerSiteSelectorProvider
 * once at the dialog level, avoiding re-render cascades.
 *
 * Screen components are rendered as functions (deferred) so they only mount when active,
 * and each screen subscribes to provider state independently.
 */
const SystemCreate = ({ customerId, siteId }: SystemCreateProps) => {
  const [open, setOpen] = useState(false)
  const [currentScreen, setCurrentScreen] = useState<ScreenId>("create-system")
  const navigate = useNavigate()
  const route = useRoute()

  const showCustomerSiteSelection = !customerId || !siteId

  const handleClose = useCallback(() => {
    setOpen(false)
    setCurrentScreen("create-system")
  }, [])

  const handleBack = useCallback(() => {
    setCurrentScreen("create-system")
  }, [])

  const handleSuccess = useCallback(
    (system: { id: string; name: string }) => {
      const systemPath = `${route.endsWith("/") ? route : route + "/"}${system.id}`
      navigate(systemPath)
      setOpen(false)
      setCurrentScreen("create-system")
    },
    [route, navigate],
  )

  // Provider wrapper injected into MultiScreenDialog — renders once, wraps all screens
  const ProviderWrapper = useMemo(() => {
    return ({ children }: { children: ReactNode }) => (
      <CustomerSiteSelectorProvider
        initialCustomerId={customerId || null}
        initialSiteId={siteId || null}
      >
        {children}
      </CustomerSiteSelectorProvider>
    )
  }, [customerId, siteId])

  const screens: Screen[] = [
    {
      id: "create-system",
      title: "Create a system",
      canGoBack: false,
      component: () => (
        <SystemCreationForm
          onSuccess={handleSuccess}
          onCancel={handleClose}
          customerId={customerId}
          siteId={siteId}
          showCustomerSiteSelection={showCustomerSiteSelection}
          onCreateCustomer={() => setCurrentScreen("create-customer")}
          onCreateSite={() => setCurrentScreen("create-site")}
          externalProvider={true}
        />
      ),
    },
    {
      id: "create-customer",
      title: "Create a customer",
      canGoBack: true,
      component: () => (
        <CustomerFormScreen onBack={handleBack} onNavigate={setCurrentScreen} />
      ),
    },
    {
      id: "create-site",
      title: "Create a site",
      canGoBack: true,
      component: () => (
        <SiteFormScreen
          fallbackCustomerId={customerId}
          onBack={handleBack}
          onNavigate={setCurrentScreen}
        />
      ),
    },
  ]

  return (
    <MultiScreenDialog
      screens={screens}
      currentScreenId={currentScreen}
      open={open}
      setOpen={setOpen}
      onClose={handleClose}
      onBack={handleBack}
      trigger={<CreateSystemButton />}
      Provider={ProviderWrapper}
    />
  )
}

export default SystemCreate

// --- Isolated screen components ---

interface CustomerFormScreenProps {
  onBack: () => void
  onNavigate: (screen: ScreenId) => void
}

const CustomerFormScreen = ({ onBack, onNavigate }: CustomerFormScreenProps) => {
  const { setCustomer, setSite } = useCustomerSiteSelectorActions()

  return (
    <CustomerCreationForm
      enableSiteCreation={true}
      siteCheckboxLabel="Also create a site for this customer"
      onSuccess={(customer) => {
        const sites = customer.sites ?? []
        setCustomer(customer.id, customer.name)
        if (sites.length > 0) {
          setSite(sites[0].id, sites[0].name)
        }
        onNavigate("create-system")
      }}
      onBack={onBack}
    />
  )
}

interface SiteFormScreenProps {
  fallbackCustomerId?: string
  onBack: () => void
  onNavigate: (screen: ScreenId) => void
}

const SiteFormScreen = ({ fallbackCustomerId, onBack, onNavigate }: SiteFormScreenProps) => {
  const { customerId } = useCustomerSiteSelectorState()
  const { setSite } = useCustomerSiteSelectorActions()

  return (
    <SimpleSiteCreationForm
      customerId={customerId || fallbackCustomerId || ""}
      onSuccess={(site) => {
        setSite(site.id, site.name)
        onNavigate("create-system")
      }}
      onBack={onBack}
    />
  )
}
